import { Node } from "@tiptap/core";

/**
 * Tiptap node extension that preserves custom HTML elements (web component
 * style tags like <my-component>) through the editor's parse/serialize
 * round-trip.
 *
 * Without this, Tiptap strips any tags it doesn't recognise.
 *
 * The node renders as an uneditable pill in the editor so users can see,
 * select, and delete components but can't accidentally break the inner
 * markup.
 */
export const CustomComponent = Node.create({
  name: "customComponent",
  group: "block",
  atom: true,
  draggable: true,

  // Low priority so standard nodes (paragraph, heading, etc.) match first
  priority: 50,

  addAttributes() {
    return {
      tagName: {
        default: null,
        // Don't render tagName as an HTML attribute
        rendered: false,
      },
      rawAttributes: {
        default: {},
        rendered: false,
      },
      innerHTML: {
        default: "",
        rendered: false,
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: "*",
        getAttrs(node) {
          if (!(node instanceof HTMLElement)) return false;

          const tag = node.tagName.toLowerCase();
          // Web component convention: must contain a hyphen
          if (!tag.includes("-")) return false;

          const rawAttributes = {};
          for (const attr of node.attributes) {
            rawAttributes[attr.name] = attr.value;
          }

          return {
            tagName: tag,
            rawAttributes,
            innerHTML: node.innerHTML,
          };
        },
      },
    ];
  },

  renderHTML({ node }) {
    const { tagName, rawAttributes, innerHTML } = node.attrs;

    // Build attributes object, parsing rawAttributes if it's a JSON string
    // (can happen on round-trip through getJSON/setContent)
    let attrs = {};
    if (typeof rawAttributes === "string") {
      try {
        attrs = JSON.parse(rawAttributes);
      } catch {
        attrs = {};
      }
    } else if (rawAttributes && typeof rawAttributes === "object") {
      attrs = { ...rawAttributes };
    }

    const el = document.createElement(tagName);
    for (const [key, value] of Object.entries(attrs)) {
      el.setAttribute(key, value);
    }
    el.innerHTML = innerHTML || "";

    return { dom: el };
  },

  addNodeView() {
    return ({ node }) => {
      const dom = document.createElement("div");
      dom.className = "tiptap-custom-component";
      dom.contentEditable = "false";

      const label = document.createElement("span");
      label.className = "tiptap-custom-component-label";
      label.textContent = `<${node.attrs.tagName}>`;
      dom.appendChild(label);

      if (node.attrs.innerHTML) {
        const preview = document.createElement("span");
        preview.className = "tiptap-custom-component-preview";
        const text = node.attrs.innerHTML.replace(/<[^>]+>/g, "").trim();
        preview.textContent =
          text.length > 60 ? text.substring(0, 60) + "…" : text;
        dom.appendChild(preview);
      }

      return { dom };
    };
  },
});
